import React, { useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { t } from 'i18next';
import styles from './CopyToClipboardButton.module.scss';

type CopyToClipboardButtonProps = {
    /**
     * The text to be copied to the clipboard.
     */
    textToCopy: string;

    /**
     * The custom button component to trigger the copy action.
     * This allows customization of the button's appearance and behavior.
     */
    ButtonComponent: React.ReactNode;
};

/**
 * A reusable button component that copies the provided text to the clipboard
 * and displays a tooltip upon successful copy.
 *
 * @param  props - The properties for the component.
 * @param  props.textToCopy - The text that will be copied to the clipboard.
 * @param props.ButtonComponent - A custom button component for triggering the copy action.
 * @returns The rendered CopyToClipboardButton component.
 */
export const CopyToClipboardButton: React.FC<CopyToClipboardButtonProps> = ({
    textToCopy,
    ButtonComponent,
}) => {
    const [tooltipVisible, setTooltipVisible] = useState(false);

    const tooltipAnimation = useSpring({
        opacity: tooltipVisible ? 1 : 0,
        transform: tooltipVisible ? 'translateY(0px)' : 'translateY(-10px)',
        config: { tension: 200, friction: 15 },
    });

    /**
     * Handles the copy action by writing the text to the clipboard.
     * Displays a tooltip to indicate the text was successfully copied.
     */
    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(textToCopy);
        setTooltipVisible(true);

        // Hide tooltip after 2 seconds
        setTimeout(() => setTooltipVisible(false), 2000);
    };

    return (
        <div className={styles.copyToClipboardWrapper}>
            <div onClick={handleCopyToClipboard}>{ButtonComponent}</div>
            {tooltipVisible && (
                <animated.span style={tooltipAnimation} className={styles.tooltip}>
                    {t('copiedToClipboard')}
                </animated.span>
            )}
        </div>
    );
};
